"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useStorefrontAuth } from "@/context/StorefrontAuthContext";
import { getAuthHeaders } from "@/lib/auth-client";
import { Store, Plus, ExternalLink, Pencil, Trash2, Truck, BarChart2 } from "lucide-react";

type StoreSummary = { slug: string; name: string; createdAt?: string };

export default function DashboardPage() {
  const { user, loading: authLoading } = useStorefrontAuth();
  const router = useRouter();
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [confirmSlug, setConfirmSlug] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) router.push("/login?mode=storefront");
  }, [user, authLoading, router]);

  const fetchStores = useCallback(async () => {
    if (!user?.id || fetchingRef.current) return;
    fetchingRef.current = true;
    setStoresError(false);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/me/stores?_t=${Date.now()}`, { headers, cache: "no-store" });
      if (!res.ok) {
        if (res.status === 401) { router.push("/login?mode=storefront"); return; }
        setStoresError(true); return;
      }
      const data = await res.json();
      setStores(data.stores ?? []);
    } catch {
      setStoresError(true);
    } finally {
      fetchingRef.current = false;
      setStoresLoading(false);
    }
  }, [user?.id, router]); // eslint-disable-line

  // Initial load
  useEffect(() => {
    if (!user?.id) return;
    setStoresLoading(true);
    fetchStores();
  }, [user?.id, fetchStores]);

  // Re-fetch when the tab regains visibility or the window regains focus
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchStores(); };
    const onFocus = () => fetchStores();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchStores]);

  const handleDelete = async (slug: string) => {
    setDeletingSlug(slug);
    setDeleteError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/stores/${encodeURIComponent(slug)}`, { method: "DELETE", headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setDeleteError(body.error ?? `Delete failed (${res.status})`);
        return;
      }
      // Confirmed deleted on server — optimistically remove then re-fetch truth from DB
      setStores(prev => prev.filter(s => s.slug !== slug));
      setConfirmSlug(null);
      router.refresh(); // clear Next.js router cache
      // Re-fetch the authoritative list so UI reflects actual DB state
      const listHeaders = await getAuthHeaders();
      const listRes = await fetch(`/api/me/stores?_t=${Date.now()}`, { headers: listHeaders, cache: "no-store" });
      if (listRes.ok) {
        const data = await listRes.json();
        setStores(data.stores ?? []);
      }
    } catch {
      setDeleteError("Network error — please try again.");
    } finally {
      setDeletingSlug(null);
    }
  };

  if (authLoading) return <div className="flex min-h-[40vh] items-center justify-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600" /></div>;
  if (!user) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : (process.env.NEXT_PUBLIC_APP_ORIGIN || "http://localhost:3000");

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-cyan-400/25">
              <Store size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">My Stores</h1>
              <p className="text-slate-500 text-sm">Manage your ecommerce storefronts</p>
            </div>
          </div>
          <Link href="/upload"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold shadow-md transition-all hover:-translate-y-0.5">
            <Plus size={15} /> New Store
          </Link>
        </div>

        {storesLoading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600 mx-auto" /></div>
        ) : storesError ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <p className="text-red-600 text-sm">Failed to load stores.</p>
            <button onClick={() => window.location.reload()} className="mt-3 text-sm font-medium text-red-700 hover:underline">Retry</button>
          </div>
        ) : stores.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-14 text-center shadow-sm">
            <Store size={48} className="mx-auto text-slate-200 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">No storefronts yet</h2>
            <p className="text-slate-500 mb-6">Upload a catalog CSV to create your first live store.</p>
            <Link href="/upload" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-800 text-white font-semibold hover:bg-slate-700 transition-all shadow-md">
              <Plus size={16} /> Upload Catalog
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {stores.map(store => {
              const storeUrl = `${origin}/s/${store.slug}`;
              return (
                <div key={store.slug} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 text-lg">{store.name}</h3>
                    <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-blue-500 hover:underline transition-colors truncate block">
                      {storeUrl}
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <a href={storeUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all">
                      <ExternalLink size={13} /> View
                    </a>
                    <Link href={`/dashboard/${store.slug}/analytics`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-violet-200 bg-violet-50 text-sm font-medium text-violet-800 hover:bg-violet-100 transition-all">
                      <BarChart2 size={13} /> Analytics
                    </Link>
                    <Link href={`/dashboard/${store.slug}/orders`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-cyan-200 bg-cyan-50 text-sm font-medium text-cyan-800 hover:bg-cyan-100 transition-all">
                      <Truck size={13} /> Orders
                    </Link>
                    <Link href={`/dashboard/${store.slug}/edit`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 text-sm font-medium text-white hover:bg-slate-700 transition-all">
                      <Pencil size={13} /> Edit
                    </Link>
                    <button onClick={() => setConfirmSlug(store.slug)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-sm font-medium text-red-500 hover:bg-red-50 transition-all">
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-sm">
          <Link href="/upload" className="font-medium text-slate-600 hover:text-slate-800 hover:underline transition-colors">
            Upload new catalog →
          </Link>
        </p>
      </div>

      {confirmSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { if (!deletingSlug) { setConfirmSlug(null); setDeleteError(null); } }} />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-base font-bold text-slate-900">Delete store?</h2>
            <p className="mt-2 text-sm text-slate-500">
              <span className="font-semibold">{stores.find(s => s.slug === confirmSlug)?.name ?? confirmSlug}</span> will be permanently deleted.
            </p>
            {deleteError && (
              <p className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs font-medium text-red-700">{deleteError}</p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => { setConfirmSlug(null); setDeleteError(null); }}
                disabled={!!deletingSlug}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                disabled={deletingSlug === confirmSlug}
                onClick={() => handleDelete(confirmSlug)}
                className="px-4 py-2 rounded-xl bg-red-500 text-sm font-medium text-white hover:bg-red-600 transition-all disabled:opacity-60"
              >
                {deletingSlug === confirmSlug ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
